import { Component, OnInit, ViewEncapsulation, ViewChild, ElementRef, HostListener, OnDestroy, AfterViewInit } from '@angular/core';
import { AppUtilService } from 'app/services/app-util.service';
import { UntypedFormControl, Validators, FormGroupDirective, NgForm } from '@angular/forms';
import { PasswordService } from 'app/services/password.service';
import { AuthenticationService } from 'app/services/authentication.service';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { Router, ActivatedRoute } from '@angular/router';
import { ResetPassword } from 'app/models/reset-password.model';
import { takeUntil } from 'rxjs/operators';
import { Subject, Subscription, timer } from 'rxjs';
import { HttpLoginRequest } from 'app/models/http-login-request';
import { VerificationResult } from 'app/models/verification-result.enum';

export class MyErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: UntypedFormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    const isSubmitted = form && form.submitted;
    return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
  }
}

@Component({
  selector: 'reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss','../app.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ResetPasswordComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('newPassword', { read: ElementRef }) newPassword: ElementRef;
  @ViewChild('cnfPassword', { read: ElementRef }) cnfPassword: ElementRef;

  public matcher = new MyErrorStateMatcher();

  private passwordRegex =  new RegExp('^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{10,})');
  private passwordEmailRegex: RegExp;

  public cnfPasswordControl: UntypedFormControl;

  public unsubscribe$ = new Subject<boolean>();
  public loading: boolean;
  public loadingMessage: string;
  public formIsSubmitted: boolean;

  public newPasswordType = 'password';
  public cnfPasswordType = 'password';
  public errorMessageToDisplay: string = '';

  public showNewPasswordDetails = true;
  public showCnfPasswordDetails: boolean;

  public isPasswordMatch = true;

  public passwordResetUUID: string; // Passing in this UUID allows you to reset the password for the web user it was created for
  public loginId: string;

  private resetPassword: ResetPassword;
  private verificationResult: VerificationResult;

  public isPassedInUrlValid: boolean;
  public passwordFailedServerValidation: boolean;
  public isLoginIdSearchFinished: boolean; // Allows us to display loading animation until we know to show the pass reset form or the expired link info

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private appUtil: AppUtilService,
    private service: PasswordService,
    private authService: AuthenticationService
  ) {
    this.appUtil.setTitle('Reset Password');
  }

  public ngOnInit(): void {
    this.listenForPasswordFieldChanges();
  }

  public ngAfterViewInit(): void {
    this.getWebUsersEmailAddressFromPasswordResetUUI();
  }

  @HostListener('window:beforeunload', ['$event'])
  public ngOnDestroy(event?): void {
    this.unsubscribe$.next(true);
  }

  public getWebUsersEmailAddressFromPasswordResetUUI() {
    // Grab the password reset uuid from a query param 'u'
    // Ex. https://www.summitholdings.com/auth/resetPassword?u=abcda5ad-abc4-490a-abc8-cd510f5eada0
    this.passwordResetUUID = this.route.snapshot.queryParamMap.get('u');

    // Make sure a param was actually passed in
    if (!this.passwordResetUUID) {
      this.isLoginIdSearchFinished = true;
      this.formIsSubmitted = true;
      this.errorMessageToDisplay = 'We could not recognize your password reset link. Please copy and paste the link from inside your password reset email. ' +
        'For additional assistance please call Customer Care 1-800-282-7648.';
      return;
    }

    // The web user clicked a link in their email that routed them here and passed in a tempUUID
    // This tempUUID is stored in WSCPASSLNK and is linked to their webUserUUID
    // We then use their webUserUUID to look up their login email address
    this.service.getLoginEmailAddressByPasswordResetUUID(this.passwordResetUUID).subscribe((data: string) => {
      // Stop the loading animation
      this.isLoginIdSearchFinished = true;

      if (data === undefined || data === '' || data === 'call-errored') {
        this.formIsSubmitted = true;
        this.errorMessageToDisplay = 'Error while resetting your password. Please try resetting your password again. For additional assistance please call Customer Care 1-800-282-7648.';
      } else if (data === 'missing-reset-link') {
        this.formIsSubmitted = true;
        this.errorMessageToDisplay = 'We could not recognize your password reset link. Please copy and paste the link from inside your password reset email. ' +
          'For additional assistance please call Customer Care 1-800-282-7648.';
      } else if (data === 'missing-email-address') {
        this.formIsSubmitted = true;
        this.errorMessageToDisplay = 'Error retrieving your email address. Please try resetting your password again. For additional assistance please call Customer Care 1-800-282-7648.';
      } else if (data === 'expired') {
        this.formIsSubmitted = true;
        this.errorMessageToDisplay = 'Your password reset link has expired and is no longer active. Please try resetting your password again.';
      } else {
        this.isPassedInUrlValid = true;
        this.loginId = data;
        if (this.loginId) {
          this.authService.isWebsiteAccessSuspended(this.loginId).subscribe((isSuspended: boolean) => {
            if (isSuspended) {
              this.router.navigate(['suspended'], { queryParams: { email: this.loginId } });
            } else {
              this.passwordEmailRegex = new RegExp('^(?!.*?' + this.loginId.toLowerCase() + ').*');
            }
          });
        }
      }
    },
      err => {
        console.log(err);
        this.isLoginIdSearchFinished = true;
        this.formIsSubmitted = true;
        this.errorMessageToDisplay = 'Unhandled error. Please try resetting your password again. For additional assistance please call Customer Care 1-800-282-7648.';
      }
    );
  }

  public onSubmit(newPassword) {
    this.loading = true;
    this.formIsSubmitted = true;
    this.isPasswordMatch = true;

    const validEntry = this.validateEntry(newPassword);
    if (validEntry) {
      this.resetPassword = new ResetPassword();
      this.resetPassword.tmpUUID = this.passwordResetUUID;
      this.resetPassword.loginId = this.loginId;
      this.resetPassword.password = this.cnfPasswordControl.value;

      this.loadingMessage = 'Updating account';
      this.service.submitNewPassword(this.resetPassword).subscribe(data => {
        if (!data || !data.url) {
          this.loading = false;
          this.loadingMessage = null;
          this.errorMessageToDisplay = 'Uncaught error while resetting your password. Please try resetting your password again. For additional assistance please call Customer Care 1-800-282-7648.'
          return;
        }

        const url: string = data.url;
        if (url === 'missing-active-account') {
          this.loading = false;
          this.loadingMessage = null;
          this.errorMessageToDisplay = 'You do not have any active accounts yet.' +
            ' You will not be able to reset your password until Summit is done reviewing and approving your account.' +
            ' This process usually takes a couple days after the initial request.' +
            ' Please contact Customer Care at 1-800-282-7648 if you have questions.';
        } else if (url === 'inactive-ldap') {
          this.loading = false;
          this.loadingMessage = null;
          this.errorMessageToDisplay = 'Please contact Customer Care at 1-800-282-7648 to have them activate your LDAP account.';
        } else if (url === 'empty-request-body') {
          this.loading = false;
          this.loadingMessage = null;
          this.errorMessageToDisplay = 'Network Error while resetting your password. Please try the link again or contact Customer Care at 1-800-282-7648.';
        } else if (url === 'expired') {
          this.loading = false;
          this.loadingMessage = null;
          this.errorMessageToDisplay = 'Your password reset link has expired.'
        } else if (url === 'passwordFailedServerValidation') {
          this.passwordFailedServerValidation = true;
          this.loading = false;
          this.loadingMessage = null;
          this.errorMessageToDisplay = 'Your password failed validation. Please use a different password.'
        } else if (url === 'updating-password-for-different-user') {
          this.loading = false;
          this.loadingMessage = null;
          this.errorMessageToDisplay = 'Error while updating your account password. Please try again or contact Customer Care at 1-800-282-7648.'
        } else {
          this.isPassedInUrlValid = true;
          // Redirect them to the URL, which is currently the Business Center web app
          this.delayBeforeRenderingURL(url);
        }
      },
        err => {
          console.log(err);
          this.loading = false;
          this.loadingMessage = null;
          this.errorMessageToDisplay = 'Unhandled error while resetting your password. Please try resetting your password again. For additional assistance please call Customer Care 1-800-282-7648.';
        }
      );
    } else {
      this.cnfPasswordControl.setErrors({'invalid': true});
      this.loading = false;
      this.loadingMessage = null;
      this.formIsSubmitted = false;
    }
  }

  private delayBeforeRenderingURL(url: string): void {
    const delayTimer: any = timer(1000, 1000);

    let delayCounter = 3; // delay 3 seconds for loading message=Your account has been updated
    const subscription1: Subscription = delayTimer.subscribe(() => {
        this.loadingMessage = 'Your account has been updated';
        delayCounter -= 1;
        if (delayCounter < 1) {
          subscription1.unsubscribe();
          delayCounter = 3; // delay 3 seconds for the loading messages in the verifyLogin()
          this.verifyLogin(); // after a successful password reset verify if verfication code is needed
          const subscription2: Subscription = delayTimer.subscribe(() => {
            delayCounter -= 1;
            if (delayCounter < 1) {
              subscription2.unsubscribe();
              this.userLogin(url);
            }
          });
        }
    });
  }

  private verifyLogin(): void {
    this.authService.request = new HttpLoginRequest(this.resetPassword.loginId);
    this.authService.jpassword = this.resetPassword.password;

    this.authService.verify().subscribe(result => {
      this.verificationResult = result;
      if (this.verificationResult === VerificationResult.TRUSTED) {
        // "trustedClientPC" cookie still active and no verification required
        this.loadingMessage = 'Redirecting to the Business Center';
      } else {
        // verification code needed and "trustedClientPC" cookie expired
        this.loadingMessage = 'Sending Verification Code';
      }
    },
    err => {
      console.error(err);
      this.router.navigate(['login']);
    });
  }

  private userLogin(url: string): void {
    if (this.resetPassword.loginId && this.resetPassword.password) {
      if (this.verificationResult === VerificationResult.SESSIONEXPIRED) {
        this.router.navigate(['sessionExpired']);
      } else if (this.verificationResult === VerificationResult.TRUSTED) {
        this.authService.userLogin(this.resetPassword.loginId, this.resetPassword.password).subscribe(() => {
          // send user to the Business Center after a successful password reset
            this.authService.setChatRequirements();
          window.location.href = decodeURIComponent(url);
        },
        err => {
          console.error(err);
          this.router.navigate(['login']);
        });
      } else {
        this.authService.generateOneTimePasscodeEmail().subscribe(isSuccess => {
          const request: HttpLoginRequest = this.authService.request;
          this.authService.request = request;
          // send user for two steps verification
          this.router.navigate(['verify']);
        },
        err => {
          console.error(err);
          this.router.navigate(['login']);
        });
      }
    }
  }

  private validateIfPasswordMatched(): void {
    const new_password = this.newPassword.nativeElement.value;
    const cnf_password = this.cnfPasswordControl.value;
    if (new_password !== cnf_password) {
      this.cnfPasswordControl.setErrors({'dontmatch': true});
      this.isPasswordMatch = false;
    } else {
      this.cnfPasswordControl.setErrors(null);
      this.isPasswordMatch = true;
    }
  }

  private validateEntry(newPassword): boolean {
    if (newPassword.passwordFormControl.hasError('required')) {
      this.showNewPasswordDetails = true;
      return false;
    }
    if (newPassword.passwordFormControl.hasError('pattern')) {
      this.showNewPasswordDetails = true;
      return false;
    }

    let isNotValid = false;
    const new_password: string = newPassword.password;
    const cnf_password: string = this.cnfPasswordControl.value;

    if (!new_password) {
      this.showNewPasswordDetails = true;
      newPassword.passwordFormControl.setErrors('required');
      isNotValid = true;
    }

    if (!this.passwordEmailRegex.test(new_password.toLowerCase())) {
      this.showNewPasswordDetails = true;
      newPassword.passwordFormControl.setErrors({'includesEmail': true});
      return false;
    } else if (!this.passwordRegex.test(new_password)) {
      this.showNewPasswordDetails = true;
      newPassword.passwordFormControl.setErrors('pattern');
      isNotValid = true;
    }

    if (isNotValid) {
      this.newPassword.nativeElement.focus();
      return false;
    }

    if (new_password !== cnf_password) {
      this.cnfPasswordControl.setErrors({'dontmatch': true});
      this.isPasswordMatch = false;
      this.cnfPassword.nativeElement.focus();
      return false;
    }

    return true;
  }

  /**
   * Show/Hide new password details
   */
  public showHideNewPasswordDetails(event) {
    this.showNewPasswordDetails = event.checked;
  }

  public onNewPasswordStrengthChanged() {
    this.validateIfPasswordMatched();
  }

  /**
   * Show/Hide new password
   */
  public onShowHideNewPassword(event: MatSlideToggleChange) {
    event.checked ? this.newPasswordType = 'text' : this.newPasswordType = 'password';
  }

  /**
   * Show/Hide confirm password
   */
  public onShowHideCnfPassword(event: MatSlideToggleChange) {
    event.checked ? this.cnfPasswordType = 'text' : this.cnfPasswordType = 'password';
  }

  private listenForPasswordFieldChanges() {
    this.cnfPasswordControl = new UntypedFormControl(null, Validators.required);
    this.cnfPasswordControl.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.validateIfPasswordMatched();
      });
  }
}

export function delay(ms: number) {
  return new Promise( resolve => setTimeout(resolve, ms) );
}
